import { useMemo } from "react";
import type { Position } from "../lib/geo";
import type { SpeedLimit } from "../lib/road";
import { SpeedLimitSign } from "./SpeedLimitSign";

type Props = {
  position: Position | null;
  limit: SpeedLimit;
  roadError?: string | null;
};

/**
 * Speed HUD — bottom-left. Big tabular number + adaptive vertical bar.
 *
 * The bar's full height represents `1.3 × current speed limit`, so the
 * limit line always sits at ~77% of the bar. As you exceed the limit the
 * fill rushes above the line and the gradient shifts toward red.
 *
 * If there's no known limit, the bar scales against a 130 km/h ceiling and
 * stays neutral.
 */
export function SpeedHud({ position, limit, roadError }: Props) {
  const speedKmh = position?.speedMs != null ? position.speedMs * 3.6 : 0;
  const limitKmh = limit.kmh;
  const ceiling = limitKmh ? limitKmh * 1.3 : 130;
  const pct = Math.min(1, speedKmh / ceiling);
  const limitPct = limitKmh ? Math.min(1, limitKmh / ceiling) : null;

  // ratio of speed to limit (1.0 = exactly at limit)
  const ratio = limitKmh ? speedKmh / limitKmh : 0.6;
  const tone = useMemo(() => {
    if (!limitKmh) return { top: "#9eb6ff", bot: "#5d7be0", glow: "rgba(112,140,255,0.45)", over: false };
    if (ratio < 0.6) return { top: "#9ee0ff", bot: "#5ab6da", glow: "rgba(102,200,235,0.45)", over: false };
    if (ratio < 0.95) return { top: "#9eff9e", bot: "#3fbf6e", glow: "rgba(70,210,120,0.55)", over: false };
    if (ratio < 1.05) return { top: "#fff7a3", bot: "#e8b04b", glow: "rgba(232,176,75,0.55)", over: false };
    if (ratio < 1.2) return { top: "#ffcc70", bot: "#ff7a45", glow: "rgba(255,140,80,0.6)", over: true };
    return { top: "#ff9a8b", bot: "#ff3b30", glow: "rgba(255,80,80,0.75)", over: true };
  }, [ratio, limitKmh]);

  const ticks = useMemo(() => {
    // tick every 20 km/h of ceiling
    const step = ceiling > 100 ? 20 : 10;
    const out: number[] = [];
    for (let v = step; v < ceiling; v += step) out.push(v);
    return out;
  }, [ceiling]);

  const roadLabel = roadError ? "Road API Error" : (limit.name || labelForHighway(limit.highway));

  return (
    <div className="speed-hud">
      <div>
        <div className="speed-number-block">
          <div
            className={`speed-number tabular ${tone.over ? "over" : ""}`}
            style={tone.over ? undefined : { background: undefined }}
          >
            {Math.round(speedKmh)}
          </div>
          <div className="speed-unit">km/h</div>
        </div>
        <div className="speed-caption">
          <span className="speed-road">{roadLabel}</span>
        </div>
      </div>

      <div className="speed-hud-right">
        <SpeedLimitSign kmh={limitKmh} over={tone.over} size={84} />

        <div
          className="speed-bar"
        style={
          {
            "--bar-top": tone.top,
            "--bar-bot": tone.bot,
            "--bar-glow": tone.glow,
          } as React.CSSProperties
        }
      >
        <div className="speed-bar-ticks">
          {ticks.map((v) => (
            <div
              key={v}
              className="speed-bar-tick"
              style={{ bottom: `${(v / ceiling) * 100}%` }}
            />
          ))}
        </div>
        <div
          className="speed-bar-fill"
          style={{ height: `${pct * 100}%` }}
        />
          {limitPct != null && (
            <div
              className="speed-bar-limit"
              data-limit={`${limitKmh} km/h`}
              style={{ bottom: `${limitPct * 100}%` }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function labelForHighway(h: string | null | undefined) {
  if (!h) return "Unknown road";
  const map: Record<string, string> = {
    motorway: "Motorway",
    motorway_link: "Motorway link",
    trunk: "Trunk",
    primary: "Primary road",
    secondary: "Secondary road",
    tertiary: "Tertiary road",
    residential: "Residential",
    living_street: "Living street",
    service: "Service road",
    unclassified: "Local road",
  };
  return map[h] || h.charAt(0).toUpperCase() + h.slice(1);
}
